import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatDistanceToNow, isPast, isToday } from "date-fns";
import { CheckCircle2, Clock, AlertTriangle, ListTodo, Calendar, Bot, Trash2 } from "lucide-react";
import type { Task, Customer } from "@/types/crm";
import { TASK_PRIORITIES } from "@/types/crm";
import { CreateTaskDialog } from "@/components/admin/CreateTaskDialog";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

type TaskWithCustomer = Task & { customer?: Pick<Customer, 'first_name' | 'last_name'> | null };

export default function TasksManagement() {
  const [tasks, setTasks] = useState<TaskWithCustomer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [deleteTarget, setDeleteTarget] = useState<TaskWithCustomer | null>(null);

  // Bulk operations
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  useEffect(() => { 
    loadTasks(); 
    const interval = setInterval(() => {
      loadTasks();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadTasks = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("tasks")
      .select("*, customer:customers(first_name, last_name)")
      .order("due_date", { ascending: true });

    if (error) {
      toast.error("Failed to load tasks");
    } else {
      setTasks((data as any[]) || []);
    }
    setIsLoading(false);
  };

  const toggleComplete = async (task: TaskWithCustomer) => {
    const isCompleting = !task.completed_at;
    const { error } = await supabase
      .from("tasks")
      .update({ completed_at: isCompleting ? new Date().toISOString() : null })
      .eq("id", task.id);

    if (error) {
      toast.error("Failed to update task");
    } else {
      toast.success(isCompleting ? "Task completed" : "Task reopened");
      loadTasks();
    }
  };

  const deleteTask = async (taskId: string) => {
    const { error } = await supabase.from("tasks").delete().eq("id", taskId);
    if (error) toast.error("Failed to delete task");
    else {
      toast.success("Task deleted");
      loadTasks();
    }
  };

  const handleBulkComplete = async () => {
    if (selectedTaskIds.length === 0) return;
    setIsBulkProcessing(true);
    const { error } = await supabase
      .from("tasks")
      .update({ completed_at: new Date().toISOString() })
      .in("id", selectedTaskIds);

    if (error) {
      toast.error("Failed to complete tasks");
    } else {
      toast.success(`Completed ${selectedTaskIds.length} tasks`);
      setSelectedTaskIds([]);
      loadTasks();
    }
    setIsBulkProcessing(false);
  };

  const handleBulkReopen = async () => {
    if (selectedTaskIds.length === 0) return;
    setIsBulkProcessing(true);
    const { error } = await supabase
      .from("tasks")
      .update({ completed_at: null })
      .in("id", selectedTaskIds);

    if (error) {
      toast.error("Failed to reopen tasks");
    } else {
      toast.success(`Reopened ${selectedTaskIds.length} tasks`);
      setSelectedTaskIds([]);
      loadTasks();
    }
    setIsBulkProcessing(false);
  };

  const handleBulkDelete = async () => {
    if (selectedTaskIds.length === 0) return;
    setIsBulkProcessing(true);
    const { error } = await supabase
      .from("tasks")
      .delete()
      .in("id", selectedTaskIds);

    if (error) {
      toast.error("Failed to delete tasks");
    } else {
      toast.success(`Deleted ${selectedTaskIds.length} tasks`);
      setSelectedTaskIds([]);
      loadTasks();
    }
    setIsBulkProcessing(false);
  };

  const parseDueDate = (d: string) => {
    // Handle both "2026-04-28" and "2026-04-28T07:30:00.000Z" formats
    if (d.includes('T')) return new Date(d);
    return new Date(d + "T00:00:00");
  };

  const filteredTasks = tasks.filter(t => {
    if (statusFilter === "pending") return !t.completed_at;
    if (statusFilter === "overdue") return !t.completed_at && isPast(parseDueDate(t.due_date));
    if (statusFilter === "completed") return !!t.completed_at;
    if (statusFilter === "today") return !t.completed_at && isToday(parseDueDate(t.due_date));
    return true;
  });

  const overdueCount = tasks.filter(t => !t.completed_at && isPast(parseDueDate(t.due_date))).length;
  const todayCount = tasks.filter(t => !t.completed_at && isToday(parseDueDate(t.due_date))).length;
  const pendingCount = tasks.filter(t => !t.completed_at).length;
  const completedCount = tasks.filter(t => !!t.completed_at).length;

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'medium': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getDueDateColor = (dueDate: string, completed: boolean) => {
    if (completed) return 'text-muted-foreground';
    if (isPast(parseDueDate(dueDate))) return 'text-red-500 font-semibold';
    if (isToday(parseDueDate(dueDate))) return 'text-amber-500 font-semibold';
    return 'text-muted-foreground';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-heading font-bold">Tasks</h1>
          <p className="text-muted-foreground">Manage follow-ups and to-dos</p>
        </div>
        <CreateTaskDialog onCreated={loadTasks} />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><ListTodo className="h-3 w-3" /> Pending</div>
          <div className="text-2xl font-bold">{pendingCount}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><AlertTriangle className="h-3 w-3" /> Overdue</div>
          <div className="text-2xl font-bold text-red-500">{overdueCount}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Calendar className="h-3 w-3" /> Due Today</div>
          <div className="text-2xl font-bold text-amber-500">{todayCount}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><CheckCircle2 className="h-3 w-3" /> Completed</div>
          <div className="text-2xl font-bold text-green-600">{completedCount}</div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {[
          { value: "pending", label: "Pending" },
          { value: "overdue", label: "Overdue" },
          { value: "today", label: "Due Today" },
          { value: "completed", label: "Completed" },
          { value: "all", label: "All" },
        ].map(f => (
          <Button
            key={f.value}
            variant={statusFilter === f.value ? "default" : "outline"}
            size="sm"
            onClick={() => setStatusFilter(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Bulk Action Controls */}
      {selectedTaskIds.length > 0 && (
        <Card className="bg-primary/5 border border-primary/20">
          <CardContent className="p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <span className="text-sm font-semibold">{selectedTaskIds.length} tasks selected</span>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <Button size="sm" variant="outline" className="flex-1 sm:flex-none h-8 text-xs" onClick={handleBulkComplete} disabled={isBulkProcessing}>
                Complete
              </Button>
              <Button size="sm" variant="outline" className="flex-1 sm:flex-none h-8 text-xs" onClick={handleBulkReopen} disabled={isBulkProcessing}>
                Reopen
              </Button>
              <Button size="sm" variant="destructive" className="flex-1 sm:flex-none h-8 text-xs" onClick={handleBulkDelete} disabled={isBulkProcessing}>
                Delete
              </Button>
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setSelectedTaskIds([])} disabled={isBulkProcessing}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Task List */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Loading tasks...</div>
      ) : (
        <div className="space-y-2">
          {filteredTasks.map(task => (
            <Card key={task.id} className={task.completed_at ? "opacity-60" : ""}>
              <CardContent className="p-4 flex items-start gap-3">
                <Checkbox
                  checked={selectedTaskIds.includes(task.id)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedTaskIds(prev => [...prev, task.id]);
                    } else {
                      setSelectedTaskIds(prev => prev.filter(id => id !== task.id));
                    }
                  }}
                  className="mt-1 flex-shrink-0"
                />
                <Checkbox
                  checked={!!task.completed_at}
                  onCheckedChange={() => toggleComplete(task)}
                  className="mt-1 flex-shrink-0 rounded-full"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`font-semibold text-sm ${task.completed_at ? 'line-through' : ''}`}>
                      {task.title}
                    </span>
                    <Badge variant="outline" className={`text-[10px] ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </Badge>
                    {task.is_automated && (
                      <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-600 border-purple-200">
                        <Bot className="h-2.5 w-2.5 mr-0.5" /> Auto
                      </Badge>
                    )}
                  </div>
                  {task.description && (
                    <p className="text-xs text-muted-foreground mb-1 line-clamp-2">{task.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs">
                    {task.customer && (
                      <span className="text-muted-foreground">
                        {task.customer.first_name} {task.customer.last_name}
                      </span>
                    )}
                    <span className={getDueDateColor(task.due_date, !!task.completed_at)}>
                      <Clock className="h-3 w-3 inline mr-0.5" />
                      {isPast(parseDueDate(task.due_date)) && !task.completed_at ? 'Overdue — ' : ''}
                      {formatDistanceToNow(parseDueDate(task.due_date), { addSuffix: true })}
                    </span>
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500 flex-shrink-0" onClick={() => setDeleteTarget(task)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </CardContent>
            </Card>
          ))}
          {filteredTasks.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No tasks found for this filter.
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete this task?"
        description={`This will permanently delete "${deleteTarget?.title}". This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) { deleteTask(deleteTarget.id); setDeleteTarget(null); } }}
        variant="destructive"
      />
    </div>
  );
}
